import { createStorage } from '@aviata/storage';
import type { Router, RouteLocationNormalized } from 'vue-router';

/**
 * Recovery for a route whose chunk failed to load. Two failure modes show up in
 * production and they need different treatment, because they run through
 * different machinery:
 *
 * 1. The route's **CSS** dep failed (`Unable to preload CSS for <url>`). That
 *    message is produced by Vite itself, so the URL is present on every engine —
 *    unlike the JS case, where WebKit omits it. Stylesheets are not governed by
 *    the ESM module map either, so re-injecting the `<link>` genuinely re-fetches
 *    and the navigation can then be retried in place, fully styled.
 *
 * 2. The route's **JS** chunk failed. Retrying is pointless: the browser
 *    memoises the failed module URL in the document's module map and will not
 *    re-request it, so a second `import()` of the same specifier rejects without
 *    touching the network. Only a fresh document clears that, which means a hard
 *    navigation.
 *
 * Planning is split from execution so the caller can report the chosen strategy
 * *before* a hard navigation starts unloading the page — otherwise the report
 * would be cancelled in flight exactly when we most want it.
 *
 * Every strategy is one-shot per route path, guarded through `sessionStorage` so
 * a persistently failing chunk cannot spin the app in a reload loop.
 */

/**
 * The attempt ledger is keyed per app, not per product.
 *
 * `sessionStorage` is scoped to the origin, and every mini app is served from
 * the same one under a different base (`/avia/`, `/railways/`, `/cabinet/`), so
 * a single shared key would put them all in one ledger. The paths recorded in it
 * are `to.fullPath`, which excludes the base — so avia's `/` and railways' `/`
 * would be the same entry, and one app spending its budget would silently deny
 * recovery to another. Hence `storagePrefix`: each app passes its own
 * (`free-avia`, `free-rw`, `free-cabinet`) and keeps a private ledger.
 */
const ATTEMPTED_RECOVERIES_KEY_SUFFIX = 'chunk-recovery-attempts';

/**
 * Built here rather than taken whole from the caller, so the key shape lives in
 * one place and no consumer can pass a ledger key that diverges between
 * planning, execution, and release.
 */
const attemptsStorageKey = (storagePrefix: string): string =>
  `${storagePrefix}:${ATTEMPTED_RECOVERIES_KEY_SUFFIX}`;

/**
 * How many times one route may trigger a reload before we stop.
 *
 * A single attempt is too few. Once a chunk has failed in this document its
 * module URL is poisoned for good, so a reload is the *only* route back to that
 * page — and a user tapping again minutes later, after a transient CDN blip has
 * passed, deserves that one more try rather than a permanently dead tab. More
 * than a couple would turn a genuinely missing chunk into a reload storm.
 */
const MAX_RELOADS_PER_PATH = 2;
const MAX_STYLESHEET_RETRIES_PER_PATH = 1;

/** Kept separate so one strategy does not consume the other's budget. */
type RecoveryKind = 'stylesheet' | 'reload';

export type ChunkRecoveryPlan =
  | { kind: 'stylesheet'; cssUrl: string }
  | { kind: 'reload' }
  | { kind: 'offline' }
  | { kind: 'already-attempted' };

/**
 * Attempt counts per `kind:path`.
 *
 * In `sessionStorage` because it has to survive the hard navigation — that is
 * the whole point: the fresh document must know a reload already happened.
 */
const readAttempts = (storagePrefix: string): Record<string, number> => {
  const stored = createStorage(sessionStorage).parse(
    attemptsStorageKey(storagePrefix),
  );
  return stored && !Array.isArray(stored) && typeof stored === 'object'
    ? (stored as Record<string, number>)
    : {};
};

const attemptCount = (
  storagePrefix: string,
  kind: RecoveryKind,
  path: string,
): number => readAttempts(storagePrefix)[`${kind}:${path}`] ?? 0;

const recordAttempt = (
  storagePrefix: string,
  kind: RecoveryKind,
  path: string,
): void => {
  const attempts = readAttempts(storagePrefix);
  const marker = `${kind}:${path}`;

  createStorage(sessionStorage).stringify(attemptsStorageKey(storagePrefix), {
    ...attempts,
    [marker]: (attempts[marker] ?? 0) + 1,
  });
};

/**
 * Forget every attempt recorded for `path`.
 *
 * Called when `path` navigates successfully, so the budget tracks *consecutive*
 * failures — the only thing that can loop. Without it the counts would live for
 * the whole `sessionStorage` lifetime, and since the webview is kept alive across
 * app launches that can be days: one transient failure would quietly spend the
 * budget for the rest of the session.
 */
export const clearRecoveryAttempts = ({
  path,
  storagePrefix,
}: {
  path: string;
  storagePrefix: string;
}): void => {
  const attempts = readAttempts(storagePrefix);
  const remaining = Object.fromEntries(
    Object.entries(attempts).filter(([marker]) => !marker.endsWith(`:${path}`)),
  );

  if (Object.keys(remaining).length === Object.keys(attempts).length) {
    return;
  }

  createStorage(sessionStorage).stringify(
    attemptsStorageKey(storagePrefix),
    remaining,
  );
};

/** The failed stylesheet URL, present only in Vite's CSS-preload message. */
export const extractPreloadedCssUrl = (error: unknown): string | null => {
  if (!(error instanceof Error)) {
    return null;
  }

  const matched = /Unable to preload CSS for (\S+)/.exec(error.message);
  return matched ? matched[1] : null;
};

/**
 * Decide how to recover, synchronously, so the failure report can name the
 * strategy before the page potentially starts unloading.
 */
export const planChunkRecovery = ({
  to,
  error,
  storagePrefix,
}: {
  to: RouteLocationNormalized;
  error: unknown;
  storagePrefix: string;
}): ChunkRecoveryPlan => {
  /**
   * Nothing can be fetched while the device is offline, and a reload would swap
   * a working screen for the webview's error page. Leave the user where they are
   * and let the report record it.
   */
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { kind: 'offline' };
  }

  const cssUrl = extractPreloadedCssUrl(error);
  if (
    cssUrl &&
    attemptCount(storagePrefix, 'stylesheet', to.fullPath) <
      MAX_STYLESHEET_RETRIES_PER_PATH
  ) {
    recordAttempt(storagePrefix, 'stylesheet', to.fullPath);
    return { kind: 'stylesheet', cssUrl };
  }

  if (
    attemptCount(storagePrefix, 'reload', to.fullPath) < MAX_RELOADS_PER_PATH
  ) {
    recordAttempt(storagePrefix, 'reload', to.fullPath);
    return { kind: 'reload' };
  }

  return { kind: 'already-attempted' };
};

/**
 * Re-request a stylesheet that failed to preload. The cache-busting query is
 * what forces a real request — without it the browser can answer from whatever
 * it already holds for that URL.
 */
const reinjectStylesheet = (url: string): Promise<boolean> =>
  new Promise(resolve => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `${url}${url.includes('?') ? '&' : '?'}chunk-retry=1`;
    link.addEventListener('load', () => resolve(true));
    link.addEventListener('error', () => resolve(false));
    document.head.appendChild(link);
  });

/**
 * Reload straight into the route the user was heading to — not `location.reload()`,
 * which would re-render the page they were leaving, since a failed navigation
 * never commits the new URL. `router.resolve` supplies the app's base path
 * (`/avia/`, `/railways/`, …) that `to.fullPath` omits.
 */
const hardNavigate = (router: Router, to: RouteLocationNormalized) => {
  window.location.assign(router.resolve(to.fullPath).href);
};

/** Carry out the planned recovery. */
export const executeChunkRecovery = async ({
  router,
  to,
  plan,
  storagePrefix,
}: {
  router: Router;
  to: RouteLocationNormalized;
  plan: ChunkRecoveryPlan;
  storagePrefix: string;
}): Promise<void> => {
  if (plan.kind === 'offline') {
    return;
  }

  /**
   * The reload budget is spent, so this route cannot be reached in this document.
   * What to do depends on where the user actually is.
   *
   * Stranded — the browser is already sitting on the failed route, which after a
   * reload means a blank screen with no app shell — so move them to home. A
   * client-side navigation, not another document load: home is a different chunk
   * and very likely fetchable, and reloading would discard state belonging to a
   * page whose own chunks are fine. Not budgeted, because it must never leave the
   * user stranded, and it cannot loop: it either reaches home or raises its own
   * error for `/`, which the ordinary path then handles.
   *
   * Otherwise the user is on a working page and simply cannot get to this one.
   * Doing nothing keeps their state; the failure is already reported.
   */
  if (plan.kind === 'already-attempted') {
    /**
     * Compared without the query and hash: `href` carries them, `pathname` never
     * does, so matching the two directly would report "not stranded" for every
     * route that has a query — and a search route always does. That silently
     * disabled the escape to home exactly where it is needed most, leaving the
     * user on the blank document.
     */
    const targetPathname = router.resolve(to.fullPath).href.split(/[?#]/)[0];
    const isStranded =
      typeof window !== 'undefined' &&
      window.location.pathname === targetPathname;

    if (isStranded && to.fullPath !== '/') {
      await router.replace('/');
    }
    return;
  }

  if (plan.kind === 'stylesheet') {
    if (await reinjectStylesheet(plan.cssUrl)) {
      /**
       * `replace` rather than `push` so the failed navigation leaves no history
       * entry for the user to land back on.
       */
      await router.replace(to.fullPath);
      return;
    }

    // Stylesheet could not be re-fetched either — fall back to a fresh document.
    if (
      attemptCount(storagePrefix, 'reload', to.fullPath) >= MAX_RELOADS_PER_PATH
    ) {
      return;
    }
    recordAttempt(storagePrefix, 'reload', to.fullPath);
  }

  hardNavigate(router, to);
};
